import { useState } from 'react';

import { isHexColour } from '../../../styles/colour';

interface ColourControlProps {
  readonly disabled?: boolean;
  readonly id: string;
  readonly label: string;
  readonly onChange: (colour: string) => void;
  readonly value: string;
}

interface ColourDraft {
  readonly baseValue: string;
  readonly value: string;
}

export function ColourControl({
  disabled = false,
  id,
  label,
  onChange,
  value,
}: ColourControlProps) {
  const [draft, setDraft] = useState<ColourDraft | null>(null);
  const [lastValue, setLastValue] = useState(value);
  if (value !== lastValue) {
    setLastValue(value);
    setDraft(null);
  }
  const draftValue = draft?.baseValue === value ? draft.value : value;
  const valid = isHexColour(draftValue);

  const commit = (nextValue: string) => {
    if (isHexColour(nextValue)) {
      setDraft(null);
      onChange(nextValue.toLowerCase());
      return;
    }
    setDraft({ baseValue: value, value: nextValue });
  };

  return (
    <div className="style-control colour-control">
      <label htmlFor={`${id}-value`}>{label}</label>
      <div className="colour-control-inputs">
        <input
          aria-label={`${label} swatch`}
          className="colour-swatch-input"
          disabled={disabled}
          onInput={(event) => commit(event.currentTarget.value)}
          type="color"
          value={value}
        />
        <input
          aria-describedby={valid ? undefined : `${id}-error`}
          aria-invalid={!valid}
          disabled={disabled}
          id={`${id}-value`}
          maxLength={7}
          onBlur={() => {
            if (!valid) setDraft(null);
          }}
          onChange={(event) => commit(event.target.value)}
          pattern="#[0-9a-fA-F]{6}"
          spellCheck={false}
          type="text"
          value={draftValue}
        />
      </div>
      {!valid ? (
        <span className="style-input-error" id={`${id}-error`} role="status">
          Use a six-digit hex colour.
        </span>
      ) : null}
    </div>
  );
}
